import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, ArrowLeft, Target, TrendUp } from "@phosphor-icons/react";

export default function ClubBag() {
  const { user } = useAuth();
  const [bag, setBag] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/stats/clubs")
      .then((r) => setBag(r.data))
      .catch((e) => { if (e.response?.status === 402) setDenied(true); });
  }, []);

  if (denied || (user && !user.is_pro)) {
    return (
      <div className="max-w-md mx-auto py-10">
        <div className="card-default p-8 text-center" data-testid="club-bag-paywall">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#0F2537] text-white flex items-center justify-center">
            <Crown weight="fill" size={28} />
          </div>
          <h1 className="font-heading font-black text-2xl mt-4">Bula Pro feature</h1>
          <p className="text-[#0F2537]/70 mt-2">Your real club bag — built from every shot you log — is part of Bula Pro.</p>
          <Link to="/pricing" data-testid="club-bag-cta-pricing" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E05D3A] text-white font-bold">
            See pricing
          </Link>
        </div>
      </div>
    );
  }

  if (!bag) return <div className="py-10">Loading…</div>;

  const maxDist = Math.max(120, ...bag.map((c) => c.max_yards || 0));
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/stats" data-testid="back-to-stats" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
          <ArrowLeft size={14} weight="bold" /> Stats
        </Link>
        <Link to="/stats/advanced" className="inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A] hover:underline">
          Advanced analytics →
        </Link>
      </div>
      <div className="rise">
        <span className="label-eyebrow">Bula Pro</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Your club gapping</h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-2xl">
          Real GPS-measured distances per club, plus a consistency percentile against every other Bula Pro who's logged 5+ shots with the same club. Lower stdev = tighter gap = more reliable yardage on the course.
        </p>
      </div>

      {bag.length === 0 ? (
        <div className="card-default p-10 text-center" data-testid="club-bag-empty">
          <Target size={32} weight="fill" className="mx-auto text-[#0F2537]/30" />
          <p className="text-sm text-[#0F2537]/60 mt-3">No shot data yet. Track a shot in your next round to start filling your bag.</p>
          <Link to="/rounds/new" className="inline-flex mt-4 items-center gap-1 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-xs font-bold hover:brightness-110">
            Start a round
          </Link>
        </div>
      ) : (
        <div className="card-default overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 border-b border-[#E2E0D8] text-[10px] uppercase tracking-wider font-bold text-[#0F2537]/55">
            <div className="col-span-3">Club</div>
            <div className="col-span-1 text-right">N</div>
            <div className="col-span-1 text-right">Avg</div>
            <div className="col-span-1 text-right">± Stdev</div>
            <div className="col-span-3">Gapping</div>
            <div className="col-span-3 text-right">Vs Pro Users</div>
          </div>
          {bag.map((c) => {
            const minPct = (c.min_yards / maxDist) * 100;
            const avgPct = (c.avg_yards / maxDist) * 100;
            const maxPct = (c.max_yards / maxDist) * 100;
            const slug = c.club.replace(/\s+/g, '-').toLowerCase();
            // Percentile colour ramp: > 75 = green badge, 50–75 = gold,
            // 25–50 = grey, < 25 = red (range work needed). Renders only when
            // both the user and the peer pool have enough samples.
            const pct = c.consistency_percentile;
            const peerCount = c.peer_count ?? 0;
            let pctClass = "bg-[#0F2537]/10 text-[#0F2537]";
            if (pct != null) {
              if (pct >= 75) pctClass = "bg-[#1E5631] text-white";
              else if (pct >= 50) pctClass = "bg-[#FFC72C] text-[#0F2537]";
              else if (pct < 25) pctClass = "bg-red-600 text-white";
            }
            return (
              <div key={c.club} data-testid={`club-row-${slug}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-[#E2E0D8] last:border-0">
                <div className="col-span-12 md:col-span-3 font-heading font-bold flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#E05D3A]" /> {c.club}
                </div>
                <div className="col-span-3 md:col-span-1 text-right text-sm font-bold">{c.n}</div>
                <div className="col-span-3 md:col-span-1 text-right text-sm font-bold text-[#E05D3A]">{c.avg_yards}</div>
                <div className="col-span-3 md:col-span-1 text-right text-sm tabular-nums" data-testid={`club-stdev-${slug}`}>
                  {c.stdev_yards != null ? `± ${c.stdev_yards}` : "—"}
                </div>
                <div className="col-span-3">
                  <div className="relative h-3 rounded-full bg-[#0F2537]/5 overflow-hidden">
                    <div className="absolute h-full bg-[#0F2537]/30 rounded-full" style={{ left: `${minPct}%`, width: `${Math.max(2, maxPct - minPct)}%` }} />
                    <div className="absolute top-1/2 -translate-y-1/2 w-2 h-5 rounded-full bg-[#E05D3A]" style={{ left: `calc(${avgPct}% - 4px)` }} title={`avg ${c.avg_yards} yds`} />
                  </div>
                  <div className="text-[10px] text-[#0F2537]/50 mt-1">{c.min_yards}–{c.max_yards} yds</div>
                </div>
                <div className="col-span-3 text-right" data-testid={`club-pct-${slug}`}>
                  {pct != null ? (
                    <div className="inline-flex flex-col items-end gap-0.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold tabular-nums ${pctClass}`} title={`More consistent than ${pct}% of Pro users with ≥5 shots`}>
                        <TrendUp size={11} weight="fill" />{pct}%
                      </span>
                      <span className="text-[9px] text-[#0F2537]/40 font-bold tabular-nums">vs {peerCount} pro{peerCount === 1 ? "" : "s"}</span>
                    </div>
                  ) : c.n < 5 ? (
                    <span className="text-[10px] text-[#0F2537]/50 font-bold" title="Log ≥5 shots with this club to unlock your gapping percentile">
                      {5 - c.n} more shot{5 - c.n === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#0F2537]/50">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {bag.length > 0 && (
        <div className="card-default p-5 space-y-3" data-testid="club-bag-insight">
          <div className="flex items-start gap-3">
            <TrendUp size={20} weight="fill" className="text-[#1E5631] shrink-0 mt-0.5" />
            <p className="text-sm text-[#0F2537]/80">
              Longest club: <b>{[...bag].sort((a,b)=>b.max_yards-a.max_yards)[0].club}</b> at {[...bag].sort((a,b)=>b.max_yards-a.max_yards)[0].max_yards} yds.
              Most-used: <b>{[...bag].sort((a,b)=>b.n-a.n)[0].club}</b> ({[...bag].sort((a,b)=>b.n-a.n)[0].n} shots).
            </p>
          </div>
          {(() => {
            // Surface the user's tightest + loosest club gapping when we have
            // percentile data for at least two clubs. Pure UX: gives them an
            // immediate "work on this" / "showcase this" recommendation.
            const ranked = bag.filter((c) => c.consistency_percentile != null);
            if (ranked.length < 2) return null;
            const best = [...ranked].sort((a, b) => b.consistency_percentile - a.consistency_percentile)[0];
            const worst = [...ranked].sort((a, b) => a.consistency_percentile - b.consistency_percentile)[0];
            return (
              <div className="flex items-start gap-3" data-testid="gapping-insight-row">
                <Target size={20} weight="fill" className="text-[#E05D3A] shrink-0 mt-0.5" />
                <p className="text-sm text-[#0F2537]/80">
                  Tightest gap: <b>{best.club}</b> (±{best.stdev_yards} yds — better than {best.consistency_percentile}% of pros).
                  {best.club !== worst.club && <> Room to work: <b>{worst.club}</b> (±{worst.stdev_yards} yds).</>}
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
